import * as React from 'react';
const { useState } = React;
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App.jsx';
import LoginModal from './LoginModal.jsx';
import authService from '../services/authService.js';
import toast from 'react-hot-toast';
import { Menu, X } from 'lucide-react';
import escudoImg from '../assets/CBR_Logo_Icono.png';
import NotificationsDropdown from './NotificationsDropdown.jsx';
/**
 * Navbar — Barra de navegación principal.
 */
export default function Navbar() {
  const { user, logout, isAdmin, isStaff } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [sectionsOpen, setSectionsOpen] = useState({
    tramites: true,
    solicitudes: true
  });

  const toggleSection = (section) => {
    setSectionsOpen(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch { /* Ignorar error */ }
    logout();
    navigate('/');
    setDropdownOpen(false);
    setDrawerOpen(false);
    toast.success('Sesión cerrada.');
  };

  const isActive = (path) => location.pathname === path;

  // Links de navegación
  const navLinks = [
    { to: '/', label: 'Inicio', icon: '🏠', always: true },
    { to: '/dashboard', label: 'Mi Dashboard', icon: '📊', staff: true },
    { to: '/dashboard-impacto', label: 'Dashboard Impacto', icon: '📈', staff: true },
    { to: '/tramites', label: 'Trámites', icon: '🗂️', auth: true, group: 'tramites' },
    { to: '/funcionario', label: 'Asignación trámites', icon: '⚙️', staff: true, group: 'tramites' },
    { to: '/tomador-fojas', label: 'Tomador de Fojas', icon: '🔒', staff: true },
    { to: '/ingreso-indice', label: 'Ingreso Índices', icon: '📝', staff: true },
    { to: '/indicepropiedad', label: 'Índice Propiedad', icon: '📖', always: true },
    { to: '/indice-sociedades', label: 'Índice Sociedades', icon: '🏢', always: true },
    { to: '/gestion-solicitudes', label: 'Gestión Solicitudes', icon: '📥', staff: true, group: 'solicitudes' },
    { to: '/gestion-archivero', label: 'Archivero', icon: '🗄️', staff: true, group: 'solicitudes' },
    { to: '/estadosolicitud', label: 'Estado Solicitudes', icon: '📋', always: true, group: isStaff() ? 'solicitudes' : null },
    { to: '/estadosolicitud', label: 'Estado Archivero Judicial', icon: '🗄️', always: true, group: isStaff() ? 'solicitudes' : null },
    { to: '/verificar', label: 'Verificar Fojas', icon: '🔍', staff: true },
    { to: '/admin/quienes-somos', label: 'Admin Quiénes Somos', icon: '🏛️', admin: true },
    { to: '/admin/comunicado', label: 'Comunicado Modal', icon: '📢', admin: true },
    { to: '/usuarios', label: 'Usuarios', icon: '👥', admin: true },

    // Otros links administrativos
    //{ to: '/firma', label: 'Firma FEA', icon: '✍️', staff: true },
    //{ to: '/creacion-indice', label: 'Creación Índice', icon: '📖', staff: true },


    // Links públicos
    { to: '/solicitudes', label: 'Solicitudes', icon: '📋', always: true },
    //{ to: '/archivero', label: 'Archivero Judicial', icon: '🗄️', always: true },
    { to: '/ingresotramite', label: 'Ingreso Trámite', icon: '✍️', always: true },
    { to: '/quienes-somos', label: 'Quiénes Somos', icon: '🏛️', always: true },
    { to: '/contacto', label: 'Contacto', icon: '✉️', always: true },
  ];

  const visibleLinks = navLinks.filter((l) => {
    const isInternal = isAdmin() || isStaff();
    const hiddenForInternal = [
      '/',
      '/solicitudes',
      '/ingresotramite',
      '/quienes-somos',
      '/contacto',
      '/creacion-indice',
      '/firma'
    ];

    if (isInternal && hiddenForInternal.includes(l.to)) {
      return false;
    }

    if (l.always) return true;
    if (l.admin) return isAdmin();
    if (l.staff) return isStaff();
    if (l.auth) return !!user;
    return false;
  });

  const renderLink = (link) => (
    <Link
      key={`${link.to}-${link.label}`}
      to={link.to}
      onClick={() => setDrawerOpen(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: '14px',
        padding: '10px 16px', borderRadius: '8px',
        textDecoration: 'none', transition: 'all 0.2s',
        background: isActive(link.to) ? 'var(--info-bg)' : 'transparent',
        color: isActive(link.to) ? 'var(--primary)' : 'var(--text-main)',
        fontWeight: isActive(link.to) ? 700 : 500,
        border: '1px solid transparent',
        borderColor: isActive(link.to) ? '#bfdbfe' : 'transparent',
        fontSize: '0.95rem'
      }}
      onMouseOver={(e) => !isActive(link.to) && (e.currentTarget.style.background = '#f1f5f9')}
      onMouseOut={(e) => !isActive(link.to) && (e.currentTarget.style.background = 'transparent')}
    >
      <span style={{ fontSize: '1.2rem', opacity: isActive(link.to) ? 1 : 0.6 }}>{link.icon}</span>
      {link.label}
    </Link>
  );

  const renderGroup = (id, label, icon) => {
    const groupLinks = visibleLinks.filter(l => l.group === id);
    if (groupLinks.length === 0) return null;
    const isOpen = sectionsOpen[id];

    return (
      <div key={id} style={{ marginBottom: '0.5rem' }}>
        <button
          onClick={() => toggleSection(id)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 16px', background: 'rgba(0,51,102,0.03)', border: 'none',
            borderRadius: '8px', cursor: 'pointer', color: 'var(--primary)',
            fontWeight: 700, fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>{icon}</span>
            {label}
          </div>
          <span>{isOpen ? '▲' : '▼'}</span>
        </button>
        {isOpen && (
          <div style={{ paddingLeft: '0.5rem', marginTop: '0.4rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
            {groupLinks.map(renderLink)}
          </div>
        )}
      </div>
    );
  };

  // Estructura del menú para staff (orden específico)
  const renderMenuContent = () => {
    if (!isStaff()) {
      return visibleLinks.filter(l => !l.group).map(renderLink);
    }

    const items = [];
    const processedGroups = new Set();

    visibleLinks.forEach(link => {
      if (link.group) {
        if (!processedGroups.has(link.group)) {
          processedGroups.add(link.group);
          const groupLabel = link.group === 'tramites' ? 'Trámites' : 'Solicitudes';
          const groupIcon = link.group === 'tramites' ? '🗂️' : '📥';
          items.push(renderGroup(link.group, groupLabel, groupIcon));
        }
      } else {
        items.push(renderLink(link));
      }
    });

    return items;
  };

  return (
    <>
      <style>{`
        .desktop-auth { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
        @media (max-width: 768px) {
          .desktop-auth { display: none !important; }
        }
      `}</style>
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 900,
        background: 'var(--bg-surface)',
        borderBottom: '1px solid var(--border-color)',
        boxShadow: 'var(--shadow-sm)',
      }}>
        <div className="container" style={{ height: '76px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {/* Hamburger Button */}
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setDrawerOpen(true)}
              style={{ padding: '0.6rem', border: '1px solid var(--border-color)', color: 'var(--text-main)', background: 'var(--bg-body)' }}
            >
              <Menu size={24} />
            </button>

            {/* Logo */}
            <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '14px', textDecoration: 'none', flexShrink: 0 }}>
              <div style={{
                width: 44, height: 44,
                background: 'var(--primary)',
                borderRadius: '8px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1.4rem',
                backgroundColor: 'white'
              }}>
                <img
                  src={import.meta.env.VITE_THEME_LOGO || escudoImg}
                  alt={`Escudo ${import.meta.env.VITE_CBR_NAME || "CBR San Carlos"}`}
                  style={{ width: '30px', height: '30px', objectFit: 'contain' }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 800, fontSize: '1.1rem', letterSpacing: '-0.02em', color: 'var(--text-main)', lineHeight: 1.2 }}>
                  {import.meta.env.VITE_CBR_NAME || "CBR San Carlos"}
                </span>

              </div>
            </Link>
          </div>

          {/* Auth — Login / Usuario dropdown (Desktop) */}
          <div className="desktop-auth">
            {user ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <NotificationsDropdown />
                <div style={{ position: 'relative' }}>
                  <button
                    id="btn-user-menu"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    background: 'var(--bg-body)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    color: 'var(--text-main)',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.9rem', fontWeight: 600,
                    transition: 'var(--transition)'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.borderColor = 'var(--primary)'}
                  onMouseOut={(e) => e.currentTarget.style.borderColor = 'var(--border-color)'}
                >
                  <div style={{
                    width: 28, height: 28,
                    background: 'var(--primary-light)',
                    color: '#fff',
                    borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.8rem', fontWeight: 700,
                  }}>
                    {(user.name || '?')[0].toUpperCase()}
                  </div>
                  <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user.name || 'Usuario'}
                  </span>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>▾</span>
                </button>

                {dropdownOpen && (
                  <div style={{
                    position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '12px', padding: '8px',
                    minWidth: 200,
                    boxShadow: 'var(--shadow-lg)',
                    zIndex: 100,
                    animation: 'fadeIn 0.15s ease',
                  }}>
                    <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border-color)', marginBottom: '6px' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4, fontWeight: 600 }}>Rol de usuario</div>
                      <span className="badge badge-info">{user.role || 'user'}</span>
                    </div>
                    <button
                      id="btn-logout"
                      onClick={handleLogout}
                      style={{
                        width: '100%', textAlign: 'left', padding: '10px 12px',
                        background: 'transparent', border: 'none',
                        color: 'var(--error)', cursor: 'pointer',
                        borderRadius: '6px', fontSize: '0.9rem',
                        fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px',
                        transition: 'background 0.2s',
                      }}
                      onMouseOver={(e) => e.currentTarget.style.background = 'var(--error-bg)'}
                      onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      🚪 Cerrar Sesión
                    </button>
                  </div>
                )}
              </div>
              </div>
            ) : (
              <button
                id="btn-login"
                className="btn btn-primary"
                onClick={() => setLoginOpen(true)}
              >
                Ingresar
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Drawer Overlay */}
      {drawerOpen && (
        <div
          onClick={() => setDrawerOpen(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(2px)',
            zIndex: 998, animation: 'fadeIn 0.2s ease'
          }}
        />
      )}

      {/* Drawer Panel */}
      <div style={{
        position: 'fixed', top: 0, bottom: 0, left: 0, width: '300px',
        background: 'var(--bg-surface)',
        borderRight: '1px solid var(--border-color)',
        boxShadow: 'var(--shadow-lg)',
        zIndex: 999,
        transform: drawerOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
        display: 'flex', flexDirection: 'column'
      }}>
        <div style={{ padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-body)' }}>
          <span style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, color: 'var(--primary)', fontSize: '1.2rem' }}>
            Menú Principal
          </span>
          <button onClick={() => setDrawerOpen(false)} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
            <X size={24} />
          </button>
        </div>

        <div style={{ flex: 1, padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem', overflowY: 'auto' }}>
          {renderMenuContent()}
        </div>

        {!user ? (
          <div style={{ padding: '1.5rem', borderTop: '1px solid var(--border-color)', background: 'var(--bg-body)' }}>
            <button
              onClick={() => { setDrawerOpen(false); setLoginOpen(true); }}
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center' }}
            >
              Ingresar
            </button>
          </div>
        ) : (
          <div style={{ padding: '1.5rem', borderTop: '1px solid var(--border-color)', background: 'var(--bg-body)' }}>
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <div style={{ fontWeight: 600, color: 'var(--primary-dark)', fontSize: '1.1rem' }}>{user.name || 'Usuario'}</div>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Rol: {user.role || 'user'}</div>
            </div>
            <button
              onClick={handleLogout}
              className="btn btn-danger"
              style={{ width: '100%', justifyContent: 'center' }}
            >
              Cerrar Sesión
            </button>
          </div>
        )}
      </div>

      {/* Modal de login */}
      {loginOpen && <LoginModal onClose={() => setLoginOpen(false)} />}
    </>
  );
}
